import Help from '../Help';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import DoneIcon from '@material-ui/icons/Done';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../../grafsync/grafsync';
import Errored from '../Errored';
import { globalStyles as styles } from '../style/gstheme';
import TitledTextField from '../TitledTextField';
import { MAX_IRI_LENGTH, RTL_REGEX } from '../utils/constants';
import { checkIRI, debounce, getGraphFromProject, makeCancelable, sanitiseText } from '../utils/utils';

// Do not allow RDF, RDFS, OWL, SHACL or SKOS
const BANNED_IRI_DOMAINS = [
    "http://www.w3.org/2004/02/skos",
    "http://www.w3.org/2008/05/skos-xl#",
    "http://www.w3.org/2002/07/owl",
    "http://www.w3.org/ns/shacl",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns",
    "http://www.w3.org/2000/01/rdf-schema#"
];

class BaseIRI extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            isValidIRI: false,
            isInExisting: false,
            componentIRI: props.initialIRI,
            checking: props.initialIRI !== ""
        };
    }

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }

    componentDidMount() {
        if (this.props.initialIRI !== "") {
            this.findIRI(this.state.componentIRI);
        }
    }

    findIRI = (value) => {
        this
            .doFind({
                value,
                graph: getGraphFromProject(this.props.project)
            })
            .promise
            .then((data) => {
                const isDuplicate = data && data.exists;
                this.setState({
                    isDuplicate: isDuplicate,
                    checked: true,
                    isValidIRI: true,
                    checking: false
                });
                this.props.onValidatedIRI(value, !isDuplicate);
            })
            .catch((e) => {
                if (e.message === "The requested IRI cannot be processed.") {
                    this.setState({
                        isDuplicate: false,
                        checked: true,
                        isValidIRI: false,
                        checking: false
                    });
                    return;
                }
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    handleIRIChange = event => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        const t = event.target;
        const trimmedValue = sanitiseText(t.value.trim());
        // Detect where one component is a subset of another at the segment level
        const isInExisting = trimmedValue !== "" &&
            this.props.existingComponents.some(ec => ec &&
                (ec.id === trimmedValue
                    || ((trimmedValue.endsWith("/") || trimmedValue.endsWith("#")) && ec.id.includes(trimmedValue))
                    || (!trimmedValue.endsWith("/") && ec.id.includes(trimmedValue + "/"))
                    || (!trimmedValue.endsWith("#") && ec.id.includes(trimmedValue + "#"))
                    || ((ec.id.endsWith("/") || ec.id.endsWith("#")) && trimmedValue.includes(ec.id))
                    || (!ec.id.endsWith("/") && trimmedValue.includes(ec.id + "/"))
                    || (!ec.id.endsWith("#") && trimmedValue.includes(ec.id + "#"))
                ));
        this.setState({
            [t.name]: trimmedValue,
            checking: true,
            isDuplicate: false,
            isInExisting: false,
            checked: false
        }, () => {
            if (isInExisting || !checkIRI(trimmedValue)
                || trimmedValue.indexOf('#') > 0
                || trimmedValue.indexOf('%') > 0
                || trimmedValue.indexOf('?') > 0
                || trimmedValue.endsWith('.')
                || trimmedValue.lastIndexOf('//') === trimmedValue.length - 2
                || trimmedValue.toLowerCase().includes('graphifi')
                || trimmedValue.toLowerCase().includes('grafsync')
                || BANNED_IRI_DOMAINS.some(bd => trimmedValue.toLowerCase().startsWith(bd))
            ) {
                this.setState({ isValidIRI: false, checking: false, checked: true, isInExisting }, () => {
                    this.props.onValidatedIRI(trimmedValue, false);
                });
            }
            else {
                if (trimmedValue.length > 0) {
                    this.props.onUpdateIRI();
                    debounce(this.findIRI, this, [trimmedValue]);
                }
            }
        });
    };

    render() {

        const { classes, help, ariaLabel } = this.props;
        const { componentIRI, isErrored, isDuplicate, checked, checking, isValidIRI, isInExisting } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        const containsRTL = componentIRI.match(RTL_REGEX);

        let icon = null;
        if (checking) {
            icon =
                <Tooltip title="Waiting for server to check IRI">
                    <InputAdornment data-test="waiting" position="end">
                        <span>...</span>
                    </InputAdornment>
                </Tooltip>;
        }
        else if (componentIRI.length > 0 && !isDuplicate && checked && isValidIRI) {
            icon =
                <InputAdornment size="small" position="end">
                    <Tooltip data-test="done-checking" title="IRI valid" className={classes.smallIconButton}>
                        <DoneIcon />
                    </Tooltip>
                </InputAdornment>;
        }
        else if (isDuplicate || (!isValidIRI && componentIRI.length > 0)) {
            icon =
                <InputAdornment position="end">
                    <WarningIcon data-test="warning" />
                </InputAdornment>;
        }

        return (
            <div data-test="base-iri">
                <TitledTextField
                    dataTest="iri"
                    name="componentIRI"
                    actions={help ? <Help position={false} textRef="IRI" /> : null}
                    title="IRI"
                    margin={true}
                    required={true}
                    ariaLabel={ariaLabel}
                    error={(isDuplicate || (!isValidIRI && componentIRI.length > 0))}
                    text={componentIRI}
                    placeholder="Enter an IRI starting with http or https"
                    maxLength={MAX_IRI_LENGTH}
                    adornment={icon}
                    errorText={containsRTL ?
                        "Right-to-left text is not supported."
                        :
                        isDuplicate ?
                            "This IRI already exists. Please change."
                            :
                            isInExisting && componentIRI.length > 5 ?
                                "This IRI is a subset of another component (or vice-versa)."
                                :
                                !isValidIRI && componentIRI.length > 0 ?
                                    "The IRI is invalid."
                                    :
                                    ""}
                    onChange={this.handleIRIChange} />
            </div>
        );
    }
}

BaseIRI.defaultProps = {
    order: false,
    useXL: false,
    help: false,
    initialIRI: "",
    existingComponents: []
};

BaseIRI.propTypes = {
    initialIRI: PropTypes.string,
    classes: PropTypes.object,
    ariaLabel: PropTypes.string,
    dataTest: PropTypes.string,
    existingComponents: PropTypes.array,
    help: PropTypes.bool,
    project: PropTypes.string.isRequired,
    onUpdateIRI: PropTypes.func.isRequired,
    onValidatedIRI: PropTypes.func.isRequired
};

export default withStyles(styles)(BaseIRI);